import React, { FC, useEffect } from "react";
import styled from "@emotion/styled";
import { Button, Form, FormInstance, Input, InputNumber, Select, Row, Col } from "antd";
import { MasterBoxData } from "services/interface";

const { TextArea } = Input;

interface SettingBoxFormEditProps {
  form: FormInstance;
  onFinish: (values: any) => void;
  isPending: boolean;
  editData: MasterBoxData | undefined;
  onClose: () => void;
}

const SettingBoxFormEdit: FC<SettingBoxFormEditProps> = ({
  form,
  onFinish,
  isPending,
  editData,
  onClose,
}) => {
  useEffect(() => {
    if (editData) {
      form.setFieldsValue({
        name: editData.name,
        description: editData.description,
        width: editData.width,
        height: editData.height,
        length: editData.length,
        weight: editData.weight,
        is_active: editData.is_active,
      });
    }
  }, [editData, form]);

  return (
    <>
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
      >
        {/* ชื่อ */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              required
              label="ชื่อ"
              name="name"
              rules={[
                {
                  required: true,
                  message: "กรุณากรอกชื่อกล่อง",
                },
                {
                  max: 100,
                  message: "ชื่อกล่องต้องไม่เกิน 100 ตัวอักษร",
                },
              ]}
            >
              <Input 
                placeholder="กรุณากรอกชื่อกล่อง" 
                maxLength={100}
                showCount
              />
            </Form.Item>
          </Col>
        </Row>

        {/* กว้าง, ยาว, สูง */}
        <Row gutter={16}>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              required
              label="กว้าง (ซ.ม.)"
              name="width"
              rules={[
                {
                  required: true,
                  message: "กรุณากรอกความกว้าง",
                },
                {
                  type: 'number',
                  min: 0.1,
                  message: "ความกว้างต้องมากกว่า 0",
                },
              ]}
            >
              <InputNumber
                placeholder="0.0"
                style={{ width: '100%' }}
                min={0.1}
                step={0.1}
                precision={2}
                maxLength={8}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              required
              label="ยาว (ซ.ม.)"
              name="length"
              rules={[
                {
                  required: true,
                  message: "กรุณากรอกความยาว",
                },
                {
                  type: 'number',
                  min: 0.1,
                  message: "ความยาวต้องมากกว่า 0",
                },
              ]}
            >
              <InputNumber
                placeholder="0.0"
                style={{ width: '100%' }}
                min={0.1}
                step={0.1}
                precision={2}
                maxLength={8}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              required
              label="สูง (ซ.ม.)"
              name="height"
              rules={[
                {
                  required: true,
                  message: "กรุณากรอกความสูง",
                },
                {
                  type: 'number',
                  min: 0.1,
                  message: "ความสูงต้องมากกว่า 0",
                },
              ]}
            >
              <InputNumber
                placeholder="0.0"
                style={{ width: '100%' }}
                min={0.1}
                step={0.1}
                precision={2}
                maxLength={8}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* น้ำหนัก + สถานะการใช้งาน */}
        <Row gutter={16}>
          <Col xs={24} sm={24} md={12}>
            <Form.Item
              label={
                <span>
                  น้ำหนัก (กรัม) <span style={{ fontSize: '12px', color: '#999' }}>ไม่บังคับ</span>
                </span>
              }
              name="weight"
              rules={[
                {
                  type: 'number',
                  min: 0,
                  message: "น้ำหนักต้องมากกว่าหรือเท่ากับ 0",
                },
              ]}
            >
              <InputNumber
                placeholder="0"
                style={{ width: '100%' }}
                min={0}
                step={0.1}
                precision={2}
                maxLength={5}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12}>
            <Form.Item
              label="สถานะการใช้งาน"
              name="is_active"
            >
              <Select
                placeholder="เลือกสถานะการใช้งาน"
                style={{ width: '100%' }}
                options={[
                  { value: true, label: 'เปิดใช้งาน' },
                  { value: false, label: 'ปิดใช้งาน' }
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* รายละเอียด */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={
                <span>
                  รายละเอียด <span style={{ fontSize: '12px', color: '#999' }}>ไม่บังคับ</span>
                </span>
              }
              name="description"
            >
              <TextArea
                placeholder="กรุณากรอกรายละเอียด"
                rows={4}
                showCount
                maxLength={500}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item>
          <WrapperButton>
            <Button
              size="large"
              onClick={onClose}
            >
              ยกเลิก
            </Button>
            <Button
              style={{ width: 120 }}
              type="primary"
              size="large"
              htmlType="submit"
              disabled={isPending}
            >
              บันทึก
            </Button>
          </WrapperButton>
        </Form.Item>
      </Form>
    </>
  );
};

export default SettingBoxFormEdit;

const WrapperButton = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background-color: #f5f5f5;
  border-top: 1px solid #e8e8e8;
  margin: 20px -24px -24px -24px;
  width: calc(100% + 48px);
`;
